import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Award, FileText, FileUp, ShieldCheck, UserRound } from "lucide-react";

import MainLayout from "@/components/MainLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { publicApi } from "@/lib/api";

const stepMeta = [
  { short: "I", title: "Persönliche Angaben", subtitle: "Stamm- und Kontaktdaten für die Aufnahmeakte" },
  { short: "II", title: "Berufliche Einordnung", subtitle: "Qualifikation, Erfahrung und fachliche Schwerpunkte" },
  { short: "III", title: "Referenzunterlagen", subtitle: "Drei Referenzgutachten zur geordneten Prüfung" },
  { short: "IV", title: "Verbindliche Erklärung", subtitle: "Zusammenfassung, Bestätigung und Versand" },
];

export default function ApplyPage({ session, onLogout }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([null, null, null]);
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const { register, watch, setValue, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      full_name: "",
      email: "",
      phone: "",
      city: "",
      profession: "",
      member_type: "Ordentliches Mitglied",
      education: "",
      years_experience: "",
      specialties: "",
      website: "",
      about: "",
      referral_source: "",
      accepts_terms: false,
    },
  });

  const currentStep = stepMeta[step];
  const progress = useMemo(() => ((step + 1) / stepMeta.length) * 100, [step]);

  const validateStep = () => {
    const values = watch();
    if (step === 0 && (!values.full_name || !values.email || !values.phone || !values.city)) {
      toast.error("Bitte die persönlichen Angaben vollständig erfassen.");
      return false;
    }
    if (step === 1 && (!values.profession || !values.education || !values.years_experience || !values.about || !values.specialties)) {
      toast.error("Bitte die berufliche Einordnung und fachlichen Angaben vollständig erfassen.");
      return false;
    }
    if (step === 2 && uploadedFiles.filter(Boolean).length < 3) {
      toast.error("Bitte drei Referenzgutachten hochladen.");
      return false;
    }
    return true;
  };

  const uploadFile = async (slot, file) => {
    const email = watch("email");
    if (!email) {
      toast.error("Bitte tragen Sie zunächst Ihre E-Mail-Adresse im ersten Verfahrensschritt ein.");
      return;
    }
    setUploadingSlot(slot);
    try {
      const formData = new FormData();
      formData.append("applicant_email", email);
      formData.append("file", file);
      const asset = await publicApi.uploadReference(formData);
      const next = [...uploadedFiles];
      next[slot] = asset;
      setUploadedFiles(next);
      toast.success(`Referenzgutachten ${slot + 1} wurde geordnet übernommen.`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Der Upload konnte nicht abgeschlossen werden.");
    } finally {
      setUploadingSlot(null);
    }
  };

  const onSubmit = async (values) => {
    if (uploadedFiles.filter(Boolean).length < 3) {
      toast.error("Bitte drei Referenzgutachten hochladen.");
      return;
    }

    setSubmitting(true);
    try {
      await publicApi.submitApplication({
        ...values,
        specialties: values.specialties.split(",").map((item) => item.trim()).filter(Boolean),
        reference_files: uploadedFiles.filter(Boolean),
      });
      toast.success("Der Mitgliedsantrag wurde ordnungsgemäß eingereicht.");
      navigate("/login");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Der Antrag konnte nicht gespeichert werden.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MainLayout
      session={session}
      onLogout={onLogout}
      pageTitle="Mitgliedschaft beantragen"
      pageDescription="Digitaler Mitgliedsantrag bei REEXPERTS mit Qualifikationsangaben und Referenzgutachten."
    >
      <div className="mx-auto max-w-7xl px-6 py-12 lg:px-8 lg:py-20">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="grid gap-8 xl:grid-cols-[0.86fr_1.14fr]">
          <aside className="space-y-6 rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm" data-testid="application-intro-panel">
            <Badge className="w-fit rounded-full border border-slate-200 bg-slate-100 text-slate-700" data-testid="application-intro-badge">
              <span className="mr-3 inline-flex items-center gap-1">
                <span className="h-2 w-4 rounded-full bg-[#2f2f2f]" />
                <span className="h-2 w-4 rounded-full bg-[#a8171b]" />
                <span className="h-2 w-4 rounded-full bg-[#d4af37]" />
              </span>
              <ShieldCheck className="mr-2 h-4 w-4" /> Aufnahmeantrag der Vereinigung
            </Badge>

            <div>
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#8a6f3b]" data-testid="application-intro-kicker">Aufnahmeakte · Prüfverfahren · Freigabe</p>
              <h1 className="mt-4 font-serif text-4xl leading-[1.04] text-slate-900 lg:text-5xl" data-testid="application-intro-heading">
                Mitgliedschaft beantragen und die REEXPERTS-Kennung nach Prüfung führen.
              </h1>
            </div>

            <p className="text-base leading-relaxed text-slate-700" data-testid="application-intro-copy">
              Der Antrag dient der geordneten Erfassung von Person, Beruf, Qualifikation, Erfahrung, Tätigkeitsschwerpunkten und Referenzunterlagen. Nach positiver Entscheidung wird die laufende REEXPERTS-ID vergeben; die weiteren Verwaltungs- und Beitragsprozesse werden im Verfahren freigegeben.
            </p>

            <p className="text-sm uppercase tracking-[0.22em] text-[#8a6f3b]" data-testid="application-intro-dach-note">
              Antragsstrecke im deutschen Verbandsstil für die D-A-CH Region.
            </p>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-1" data-testid="application-benefits-requirements-grid">
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5" data-testid="application-benefits-box">
                <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#8a6f3b]">Mitgliedschaftsvorteile</p>
                <ul className="mt-4 space-y-2 text-sm leading-relaxed text-slate-700">
                  <li data-testid="application-benefit-item-1">Sichtbare REEXPERTS-Kennung im geordneten Verbandsauftritt</li>
                  <li data-testid="application-benefit-item-2">Einbindung in die Experten-Suche und in die geordnete Verbandskommunikation</li>
                  <li data-testid="application-benefit-item-3">Fortbildungsrahmen, Verfahrenssicherheit und fachliche Positionierung</li>
                </ul>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5" data-testid="application-requirements-box">
                <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#8a6f3b]">Aufnahmevoraussetzungen</p>
                <ul className="mt-4 space-y-2 text-sm leading-relaxed text-slate-700">
                  <li data-testid="application-requirement-item-1">Geordnete Angaben zu Person, Beruf, Ausbildung und Erfahrung</li>
                  <li data-testid="application-requirement-item-2">Darstellung der Tätigkeitsschwerpunkte und fachlichen Einordnung</li>
                  <li data-testid="application-requirement-item-3">Vorlage von drei Referenzgutachten zur internen Prüfung</li>
                </ul>
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5" data-testid="application-seal-preview">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#8a6f3b]">Kennzeichnungsbeispiel</p>
              <div className="mt-3 flex items-center gap-2" data-testid="application-seal-flag-accent">
                <span className="h-[3px] w-8 rounded-full bg-[#2f2f2f]" />
                <span className="h-[3px] w-8 rounded-full bg-[#a8171b]" />
                <span className="h-[3px] w-8 rounded-full bg-[#d4af37]" />
              </div>
              <div className="mt-4 flex items-center gap-3">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-900">
                  <Award className="h-6 w-6" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">REEXPERTS-2026-00X</p>
                  <p className="text-sm text-slate-500">Wird erst nach positiver Prüfung und Freigabe vergeben</p>
                </div>
              </div>
            </div>
          </aside>

          <section className="rounded-[2rem] border border-[#d4af37]/20 bg-white p-8 shadow-sm" data-testid="application-form-panel">
            <div className="space-y-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.35em] text-slate-500" data-testid="application-step-kicker">Mehrstufiger Antrag</p>
                  <h2 className="mt-3 font-serif text-3xl tracking-tight text-slate-950" data-testid="application-step-heading">{currentStep.short}. {currentStep.title}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600" data-testid="application-step-description">{currentStep.subtitle}</p>
                </div>
                <Badge className="rounded-full border border-[#d4af37]/30 bg-[#e5d3b3]/10 text-[#8c6f3d]" data-testid="application-progress-badge">Verfahrensstufe {step + 1} / {stepMeta.length}</Badge>
              </div>

              <div className="grid gap-3 md:grid-cols-4" data-testid="application-stepper-grid">
                {stepMeta.map((item, index) => (
                  <div
                    key={item.short}
                    className={`rounded-[1.4rem] border px-4 py-4 ${
                      index === step ? "border-[#d4af37]/40 bg-[#f7f1e6]" : "border-slate-200 bg-slate-50"
                    }`}
                    data-testid={`application-stepper-item-${index + 1}`}
                  >
                    <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#8c6f3d]">{item.short}</p>
                    <p className="mt-2 text-sm font-semibold text-slate-950">{item.title}</p>
                  </div>
                ))}
              </div>

              <Progress value={progress} className="h-2 bg-slate-100" data-testid="application-progress-bar" />
            </div>

            <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
              {step === 0 && (
                <Card className="border-slate-200 shadow-none" data-testid="application-step-card-personal">
                  <CardContent className="grid gap-5 p-6 md:grid-cols-2">
                    <div className="md:col-span-2 flex items-center gap-3 text-slate-700" data-testid="application-personal-intro">
                      <UserRound className="h-5 w-5 text-[#8c6f3d]" />
                      <p className="text-sm leading-relaxed">Bitte erfassen Sie die persönlichen Grunddaten vollständig und in der Form, in der sie für die Aufnahmeakte und spätere Verbandskommunikation geführt werden sollen.</p>
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-800">Vollständiger Name</label>
                      <Input {...register("full_name", { required: true })} data-testid="application-full-name-input" />
                      {errors.full_name && <p className="mt-2 text-sm text-red-500">Bitte den vollständigen Namen angeben.</p>}
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-800">E-Mail</label>
                      <Input type="email" {...register("email", { required: true })} data-testid="application-email-input" />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-800">Telefon</label>
                      <Input {...register("phone", { required: true })} data-testid="application-phone-input" />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-800">Ort</label>
                      <Input {...register("city", { required: true })} data-testid="application-city-input" />
                    </div>
                  </CardContent>
                </Card>
              )}

              {step === 1 && (
                <Card className="border-slate-200 shadow-none" data-testid="application-step-card-professional">
                  <CardContent className="grid gap-5 p-6 md:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-800">Berufliche Bezeichnung</label>
                      <Input {...register("profession", { required: true })} data-testid="application-profession-input" />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-800">Mitgliedsart</label>
                      <select {...register("member_type")} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm" data-testid="application-member-type-select">
                        <option value="Ordentliches Mitglied">Ordentliches Mitglied</option>
                        <option value="Assoziiertes Mitglied">Assoziiertes Mitglied</option>
                        <option value="Makler mit Weiterbildung">Makler mit Sachverständigen-Weiterbildung</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="mb-2 block text-sm font-medium text-slate-800">Ausbildung / Qualifikation</label>
                      <Textarea {...register("education", { required: true })} data-testid="application-education-textarea" />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-800">Berufserfahrung</label>
                      <Input {...register("years_experience", { required: true })} data-testid="application-experience-input" />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-800">Website (optional)</label>
                      <Input {...register("website")} data-testid="application-website-input" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="mb-2 block text-sm font-medium text-slate-800">Fachgebiete (kommagetrennt)</label>
                      <Input {...register("specialties", { required: true })} placeholder="z. B. Verkehrswertgutachten, Bauschäden" data-testid="application-specialties-input" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="mb-2 block text-sm font-medium text-slate-800">Kurzvorstellung / fachliche Einordnung</label>
                      <Textarea {...register("about", { required: true })} data-testid="application-about-textarea" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="mb-2 block text-sm font-medium text-slate-800">Empfehlung / Vermittlung (optional)</label>
                      <Input {...register("referral_source")} data-testid="application-referral-source-input" />
                    </div>
                  </CardContent>
                </Card>
              )}

              {step === 2 && (
                <Card className="border-slate-200 shadow-none" data-testid="application-step-card-references">
                  <CardContent className="space-y-5 p-6">
                    <div className="flex items-center gap-3 text-slate-700" data-testid="application-files-description">
                      <FileText className="h-5 w-5 text-[#8c6f3d]" />
                      <p className="text-sm leading-relaxed">Bitte übermitteln Sie drei Referenzgutachten zur geordneten fachlichen Prüfung. Zulässig sind PDF-, DOC- oder DOCX-Dateien.</p>
                    </div>

                    <div className="grid gap-4 md:grid-cols-3">
                      {[0, 1, 2].map((slot) => (
                        <Card key={slot} className="border-slate-200 bg-slate-50 shadow-none" data-testid={`application-upload-card-${slot + 1}`}>
                          <CardContent className="space-y-4 p-5">
                            <div>
                              <p className="font-mono text-xs uppercase tracking-[0.24em] text-[#8c6f3d]">Referenz {slot + 1}</p>
                              <p className="mt-2 font-semibold text-slate-950">Gutachten zur Prüfakte</p>
                            </div>
                            <label className="flex cursor-pointer flex-col items-center justify-center gap-3 rounded-3xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
                              <FileUp className="h-6 w-6 text-[#8c6f3d]" />
                              Datei auswählen und hochladen
                              <input
                                type="file"
                                className="hidden"
                                accept=".pdf,.doc,.docx"
                                onChange={(event) => event.target.files?.[0] && uploadFile(slot, event.target.files[0])}
                                data-testid={`application-reference-file-input-${slot + 1}`}
                              />
                            </label>
                            <div className="rounded-2xl bg-white p-3 text-sm text-slate-700" data-testid={`application-upload-status-${slot + 1}`}>
                              {uploadingSlot === slot ? "Upload wird verarbeitet ..." : uploadedFiles[slot]?.original_filename || "Noch keine Datei hinterlegt"}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {step === 3 && (
                <Card className="border-slate-200 shadow-none" data-testid="application-step-card-confirmation">
                  <CardContent className="space-y-5 p-6">
                    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5" data-testid="application-summary-box">
                      <p className="font-semibold text-slate-950">Zusammenfassung der Aufnahmeakte</p>
                      <p className="mt-3 text-sm text-slate-600">{watch("full_name")} · {watch("profession")} · {watch("city")}</p>
                      <p className="mt-2 text-sm text-slate-600">Fachgebiete: {watch("specialties") || "Noch nicht ausgefüllt"}</p>
                      <p className="mt-2 text-sm text-slate-600">Referenzgutachten hinterlegt: {uploadedFiles.filter(Boolean).length} / 3</p>
                    </div>

                    <div className="flex items-start gap-3 rounded-3xl border border-slate-200 p-4">
                      <Checkbox checked={watch("accepts_terms")} onCheckedChange={(checked) => setValue("accepts_terms", Boolean(checked))} data-testid="application-accepts-terms-checkbox" />
                      <div>
                        <p className="font-medium text-slate-900">Ich bestätige Satzung, Datenschutz, Nutzervereinbarung und das geordnete Prüfverfahren.</p>
                        <p className="text-sm text-slate-500">Der Antrag wird intern geprüft. Bei positiver Entscheidung wird die Mitgliedschaft samt Beitragsfreigabe im Verfahren angelegt.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                <Button type="button" variant="outline" disabled={step === 0} onClick={() => setStep((current) => Math.max(0, current - 1))} className="rounded-full" data-testid="application-back-button">
                  Zurück
                </Button>

                <div className="flex gap-3">
                  {step < stepMeta.length - 1 ? (
                    <Button type="button" onClick={() => validateStep() && setStep((current) => Math.min(stepMeta.length - 1, current + 1))} className="rounded-full bg-slate-950 text-white hover:bg-slate-800" data-testid="application-next-button">
                      Nächste Verfahrensstufe
                    </Button>
                  ) : (
                    <Button type="submit" disabled={submitting || !watch("accepts_terms")} className="rounded-full bg-[#e5d3b3] text-[#121212] hover:bg-[#f4e7d3]" data-testid="application-submit-button">
                      {submitting ? "Antrag wird übermittelt ..." : "Antrag verbindlich einreichen"}
                    </Button>
                  )}
                </div>
              </div>
            </form>
          </section>
        </motion.div>
      </div>
    </MainLayout>
  );
}
